interface InterviewerData {
  availability?: { start: string; end: string }[];
  busyEvents?: any[];
  calenderConnected?: boolean;
  isZeroEvents?: boolean; // Indicates if calendar events fetched resulted in zero events
  cachedAt?: number;
}

const normalizeId = (id: string | number): string => String(id);

/**
 * Process changes in interviewer list, handling additions, removals, and caching.
 * Determines which interviewers need fetching based *only* on existing data and cache state.
 * @param currentWeekRange - Current week range identifier
 * @param interviewersData - Current interviewers data state
 * @param interviewerIds - List of interviewer IDs currently required
 * @param tempCacheRef - Ref for caching removed interviewer data
 * @param timeoutIdsRef - Ref for timeout IDs associated with the cache
 * @param clearCache - Function to clear cache entries
 * @param setInterviewersData - Function to update interviewersData state
 * @param CACHE_TIMEOUT - Cache timeout in milliseconds
 * @returns Object containing interviewers to fetch
 */
export function schProcessInterviewerChanges({
  currentWeekRange,
  interviewersData,
  interviewerIds,
  tempCacheRef,
  timeoutIdsRef,
  clearCache,
  setInterviewersData,
  CACHE_TIMEOUT,
  timeZone,
}: {
  currentWeekRange?: string;
  interviewersData: Record<string, Record<string, InterviewerData>>;
  interviewerIds: (string | number)[];
  tempCacheRef: React.MutableRefObject<
    Record<string, Record<string, InterviewerData>>
  >;
  timeoutIdsRef: React.MutableRefObject<Record<string, NodeJS.Timeout>>;
  clearCache: (key?: string) => void;
  setInterviewersData: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, InterviewerData>>>
  >;
  CACHE_TIMEOUT: number;
  timeZone?: string;
}) {
  // Determine if availability fetch is expected based on timeZone
  const requiresAvailabilityFetch = timeZone !== 'local';

  // If no current week range, assume all need fetching initially
  if (!currentWeekRange) {
    // We don't manage inProgressRef here anymore
    return { toFetch: [...interviewerIds] };
  }

  // Normalize IDs to string for consistent comparison
  const normalizedInterviewerIds = interviewerIds.map(normalizeId);
  const normalizedInterviewerIdsSet = new Set(normalizedInterviewerIds); // Use Set for faster lookups

  // Get current interviewers from state for the target week
  const weekData = interviewersData[currentWeekRange] || {};
  const currentInterviewersInWeekData = Object.keys(weekData);

  // --- Handle Removed Interviewers (Cache Logic) ---
  const removedInterviewers = currentInterviewersInWeekData.filter(
    (id) => !normalizedInterviewerIdsSet.has(id)
  );

  if (removedInterviewers.length > 0) {
    let weekDataChanged = false;
    const updatedWeekData = { ...(weekData || {}) }; // Operate on week data copy

    // Initialize cache for current week if needed
    if (!tempCacheRef.current[currentWeekRange]) {
      tempCacheRef.current[currentWeekRange] = {};
    }

    removedInterviewers.forEach((id) => {
      const currentData = updatedWeekData[id];

      // Modified completeness check for caching - only cache if expected data is present
      const isAvailabilityPresentIfNeeded =
        !requiresAvailabilityFetch ||
        (currentData && currentData.availability !== undefined);
      const hasEventData =
        currentData &&
        currentData.busyEvents !== undefined &&
        currentData.calenderConnected !== undefined;

      if (isAvailabilityPresentIfNeeded && hasEventData) {
        // Cache the data with timestamp
        // eslint-disable-next-line no-useless-escape
        const cacheKey = `<span class="math-inline">\{currentWeekRange\}\_</span>{id}`;
        tempCacheRef.current[currentWeekRange][id] = {
          ...currentData, // Cache the actual data
          cachedAt: Date.now(),
        };

        // Clean up old timeouts
        if (timeoutIdsRef.current[cacheKey]) {
          clearTimeout(timeoutIdsRef.current[cacheKey]);
        }

        // Set new timeout for cache expiration
        timeoutIdsRef.current[cacheKey] = setTimeout(() => {
          clearCache(cacheKey);
        }, CACHE_TIMEOUT);
      }

      // Remove from the week's data copy
      delete updatedWeekData[id];
      weekDataChanged = true;

      // NOTE: We do NOT modify inProgressInterviewersRef here. Cleanup should happen
      // within the fetch hook instance when its operations complete or are cancelled.
    });

    // Update state immutably only if changes occurred for the week
    if (weekDataChanged) {
      setInterviewersData((prev) => ({
        ...prev,
        [currentWeekRange]: updatedWeekData,
      }));
    }
  }

  // --- Determine Interviewers to Fetch or Restore ---
  const toRestore: string[] = []; // Store normalized IDs
  const toFetchSet = new Set<string | number>(); // Store original IDs

  normalizedInterviewerIds.forEach((normalizedId) => {
    // Find the original ID (string or number) corresponding to the normalized string ID
    const originalId = interviewerIds.find(
      (id) => normalizeId(id) === normalizedId
    );
    if (originalId === undefined) return; // Should not happen if interviewerIds is correct

    // 1. Check if data already exists and is complete in the main state
    const currentData = weekData[normalizedId];

    // --- Modified Completeness Check for Current Data ---
    const isAvailabilityConsideredComplete =
      !requiresAvailabilityFetch ||
      (currentData && currentData.availability !== undefined);
    const isEventDataComplete =
      currentData &&
      currentData.busyEvents !== undefined &&
      currentData.calenderConnected !== undefined;
    const isDataComplete =
      isAvailabilityConsideredComplete && isEventDataComplete;

    if (isDataComplete) {
      // Data is present and complete, no action needed.
      return;
    }

    const cachedData = tempCacheRef.current[currentWeekRange]?.[normalizedId];
    const isCacheFresh =
      cachedData?.cachedAt && Date.now() - cachedData.cachedAt < CACHE_TIMEOUT;

    // --- Modified Completeness Check for Cached Data ---
    const isCacheAvailabilityComplete =
      !requiresAvailabilityFetch ||
      (cachedData && cachedData.availability !== undefined);
    const isCacheEventDataComplete =
      cachedData &&
      cachedData.busyEvents !== undefined &&
      cachedData.calenderConnected !== undefined;
    const isCacheValidAndComplete =
      isCacheFresh && isCacheAvailabilityComplete && isCacheEventDataComplete;

    if (isCacheValidAndComplete) {
      toRestore.push(normalizedId);
    } else {
      toFetchSet.add(originalId);
    }
  });

  // --- Restore from Cache ---
  if (toRestore.length > 0) {
    setInterviewersData((prev) => {
      const updatedData = { ...prev };
      const updatedWeekData = { ...(updatedData[currentWeekRange] || {}) };
      let weekDataChanged = false;

      toRestore.forEach((normalizedId) => {
        const cachedItem =
          tempCacheRef.current[currentWeekRange]?.[normalizedId];
        if (cachedItem) {
          // Take everything except cachedAt
          const { cachedAt, ...dataToRestore } = cachedItem;
          updatedWeekData[normalizedId] = { ...dataToRestore };
          weekDataChanged = true;

          // Remove the restored item from the cache immediately
          delete tempCacheRef.current[currentWeekRange]?.[normalizedId];

          // Clean up the associated timeout
          const cacheKey = `${currentWeekRange}_${normalizedId}`;
          if (timeoutIdsRef.current[cacheKey]) {
            clearTimeout(timeoutIdsRef.current[cacheKey]);
            delete timeoutIdsRef.current[cacheKey];
          }

          // NOTE: Do not modify inProgressInterviewersRef. If a fetch for this ID
          // was somehow started, let it complete/cancel naturally. Restoration
          // means we *now* have the data.
        }
      });

      if (weekDataChanged) {
        updatedData[currentWeekRange] = updatedWeekData;
        return updatedData;
      }
      return prev; // No changes needed if restoration failed somehow
    });
  }

  // --- Return IDs to Fetch ---
  // Return the unique list of original IDs that need fetching.
  return { toFetch: Array.from(toFetchSet) };
}
